import { Badge, Box, Button, Code, HStack } from '@chakra-ui/react'
import { Check, Copy } from 'lucide-react'
import { useState } from 'react'
import type { PayloadFormat } from '@/lib/mqtt-types'
import { formatPayload, usePayloadDetection } from './hooks/usePayloadDetection'

interface PayloadViewerProps {
  payload: string
  maxHeight?: string
  showFormatSelector?: boolean
}

const formatLabels: Record<PayloadFormat, string> = {
  json: 'JSON',
  xml: 'XML',
  hex: 'HEX',
  base64: 'Base64',
  text: 'Text',
}

const formatColors: Record<PayloadFormat, string> = {
  json: 'blue',
  xml: 'purple',
  hex: 'orange',
  base64: 'cyan',
  text: 'gray',
}

export function PayloadViewer({
  payload,
  maxHeight = '200px',
  showFormatSelector = true,
}: PayloadViewerProps) {
  const analysis = usePayloadDetection(payload)
  const [selectedFormat, setSelectedFormat] = useState<PayloadFormat | null>(null)
  const [copied, setCopied] = useState(false)

  const displayFormat = selectedFormat || analysis.format
  const displayContent = selectedFormat
    ? formatPayload(payload, selectedFormat)
    : analysis.formatted

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(payload)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  return (
    <Box>
      <HStack justify="space-between" mb={2}>
        <HStack gap={2}>
          <Badge colorPalette={formatColors[analysis.format]} size="sm">
            {formatLabels[analysis.format]}
          </Badge>
          <Box fontSize="xs" color="content.secondary">
            {analysis.size} bytes
          </Box>
        </HStack>

        <HStack gap={1}>
          {showFormatSelector && analysis.format !== displayFormat && (
            <Button size="xs" variant="ghost" onClick={() => setSelectedFormat(null)}>
              Auto
            </Button>
          )}
          <Button size="xs" variant="ghost" onClick={handleCopy} title="Copy to clipboard">
            {copied ? <Check size={14} /> : <Copy size={14} />}
          </Button>
        </HStack>
      </HStack>

      {showFormatSelector && (
        <HStack gap={1} mb={2} flexWrap="wrap">
          {(['json', 'xml', 'hex', 'base64', 'text'] as PayloadFormat[]).map((format) => (
            <Button
              key={format}
              size="xs"
              variant={displayFormat === format ? 'solid' : 'ghost'}
              colorPalette={displayFormat === format ? formatColors[format] : undefined}
              onClick={() => setSelectedFormat(format)}
            >
              {formatLabels[format]}
            </Button>
          ))}
        </HStack>
      )}

      <Box bg="bg.subtle" borderRadius="md" p={3} maxH={maxHeight} overflow="auto">
        <Code
          display="block"
          whiteSpace="pre-wrap"
          wordBreak="break-all"
          fontSize="xs"
          bg="transparent"
        >
          {displayContent}
        </Code>
      </Box>
    </Box>
  )
}

export default PayloadViewer
